/**
 * @module features/clients/components/ClientCard
 * @description Single client card component supporting mobile & tablet card views.
 */
import * as React from "react";
import {
  Eye,
  Edit,
  UserPlus,
  ShieldAlert,
  Trash2,
  MapPin,
  UserCheck,
  Copy,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClientSummary } from "../types";
import { ClientQuotaGauge } from "./ClientQuotaGauge";

export interface ClientCardProps {
  client: ClientSummary;
  onView: (client: ClientSummary) => void;
  onEdit: (client: ClientSummary) => void;
  onLinkUsers: (client: ClientSummary) => void;
  onToggleStatus: (client: ClientSummary) => void;
  onDelete: (client: ClientSummary) => void;
}

export function ClientCard({
  client,
  onView,
  onEdit,
  onLinkUsers,
  onToggleStatus,
  onDelete,
}: ClientCardProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      navigator.clipboard.writeText(client.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ignore
    }
  };
  const statusPill =
    client.status === "active"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
      : client.status === "suspended"
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
      : "bg-muted text-muted-foreground border-border";

  return (
    <div
      onClick={() => onView(client)}
      className="rounded-xl border border-border/80 bg-card p-3.5 shadow-xs space-y-3 cursor-pointer active:scale-[0.99] transition-transform"
    >
      {/* Top row: Monogram/Logo, Organization Name, Status Pill */}
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
          {client.logo_url ? (
            <img
              src={client.logo_url}
              alt={`${client.org_name} logo`}
              className="h-10 w-10 rounded-xl object-contain border border-border/80 bg-white p-1 shrink-0 shadow-xs"
              loading="lazy"
            />
          ) : (
            <div
              className="h-10 w-10 rounded-xl border border-border/80 flex items-center justify-center font-bold text-xs shrink-0 shadow-xs"
              style={{
                backgroundColor: `${client.theme_color}18`,
                color: client.theme_color,
              }}
            >
              {client.org_name.substring(0, 2).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-semibold text-sm text-foreground truncate">
                {client.org_name}
              </h3>
              <span
                className="h-2 w-2 rounded-full shrink-0 shadow-xs ring-1 ring-border"
                style={{ backgroundColor: client.theme_color }}
              />
            </div>
            <p className="text-xs text-muted-foreground font-mono truncate">
              {client.org_email_id}
            </p>
            <div className="flex items-center gap-1.5 pt-1">
              <button
                type="button"
                onClick={handleCopyId}
                className="inline-flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-foreground bg-muted/60 active:bg-muted px-2 py-0.5 rounded border border-border/70 transition-colors group select-all shadow-2xs"
                title="Click to copy Client Organization ID"
              >
                <span className="font-semibold text-foreground">ID:</span>
                <span className="font-mono tracking-tight">{client.id}</span>
                {copied ? (
                  <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                ) : (
                  <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100 shrink-0" />
                )}
              </button>
            </div>
          </div>
        </div>

        <span
          className={`inline-flex items-center gap-1 text-[10px] font-mono font-medium px-2 py-0.5 rounded-full border shrink-0 capitalize ${statusPill}`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              client.status === "active"
                ? "bg-emerald-500"
                : client.status === "suspended"
                ? "bg-amber-500"
                : "bg-zinc-400"
            }`}
          />
          {client.status}
        </span>
      </div>

      {/* Middle row: Quota meter */}
      <div className="pt-2 border-t border-border/50">
        <ClientQuotaGauge
          activeCount={client.active_user_count}
          maxLimit={client.max_user_limit}
        />
      </div>

      {/* Meta details: Contact person & location */}
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
        <div className="flex items-center gap-1 truncate max-w-[180px]">
          <UserCheck className="h-3 w-3 shrink-0" />
          <span className="truncate">{client.contact_person_name}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <MapPin className="h-3 w-3 shrink-0 opacity-70" />
          <span>{client.city}</span>
        </div>
      </div>

      {/* Bottom Actions Row: Stop propagation so individual buttons function */}
      <div
        className="flex items-center justify-between pt-2 border-t border-border/50 gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => onView(client)}
          className="h-9 px-3 text-xs gap-1.5 flex-1"
        >
          <Eye className="h-3.5 w-3.5" />
          <span>Inspect</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(client)}
          className="h-9 px-3 text-xs gap-1.5 flex-1"
        >
          <Edit className="h-3.5 w-3.5" />
          <span>Edit</span>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onLinkUsers(client)}
          aria-label="Link users"
          className="h-9 w-9 text-muted-foreground hover:text-foreground shrink-0"
        >
          <UserPlus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onToggleStatus(client)}
          aria-label="Toggle status"
          className="h-9 w-9 text-muted-foreground hover:text-amber-600 shrink-0"
        >
          <ShieldAlert className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(client)}
          aria-label="Delete organization"
          className="h-9 w-9 text-muted-foreground hover:text-destructive shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
